// ─────────────────────────────────────
// Enums
// ─────────────────────────────────────

export type MethodType = 'flat' | 'strip' | 'modified_strip' | 'custom_upload';
export const METHOD_FLAT: MethodType = 'flat';
export const METHOD_STRIP: MethodType = 'strip';
export const METHOD_MODIFIED_STRIP: MethodType = 'modified_strip';
export const METHOD_CUSTOM_UPLOAD: MethodType = 'custom_upload';

export type StripDateMode = 'most_recent' | 'as_of';
export const STRIP_MODE_MOST_RECENT: StripDateMode = 'most_recent';
export const STRIP_MODE_AS_OF: StripDateMode = 'as_of';

export type FallbackType =
  'discrete_price' | 'hold_last_strip' | 'hold_last_value';
export const FALLBACK_DISCRETE_PRICE: FallbackType = 'discrete_price';
export const FALLBACK_HOLD_LAST_STRIP: FallbackType = 'hold_last_strip';
export const FALLBACK_HOLD_LAST_VALUE: FallbackType = 'hold_last_value';

export type DeckType = 'calendar_dates' | 'relative';
export const DECK_TYPE_CALENDAR: DeckType = 'calendar_dates';
export const DECK_TYPE_RELATIVE: DeckType = 'relative';

export type PriceDeckStatus = 'draft' | 'active' | 'archived';

// ─────────────────────────────────────
// Types
// ─────────────────────────────────────

/** Facet option for filter dropdowns. */
export interface PriceDeckFacetItem {
  id: string;
  value: string;
}

/** Facets returned by the Price Decks list query. */
export interface PriceDeckFacets {
  createdByOptions: PriceDeckFacetItem[];
}

/** Row in a price deck preview schedule. */
export interface PriceDeckRow {
  month: string;
  oilPrice: number;
  gasPrice: number;
  source: string;
}

/** Stats returned by the price deck preview endpoint. */
export interface PriceDeckPreviewStats {
  currentOil: number | null;
  currentGas: number | null;
  avg12moOil: number | null;
  avg12moGas: number | null;
  avg60moOil: number | null;
  avg60moGas: number | null;
}

/** Response from POST /price_decks/preview. */
export interface PriceDeckPreviewResponse {
  rows: PriceDeckRow[];
  stats: PriceDeckPreviewStats;
}

/** Config JSONB stored on the price deck record. Keys are snake_case (raw hash from jbuilder). */
export interface PriceDeckConfig {
  oil_flat_price?: number;
  gas_flat_price?: number;
  strip_source?: string;
  oil_scale_factor?: number;
  oil_fallback_type?: FallbackType;
  gas_scale_factor?: number;
  gas_fallback_type?: FallbackType;
  oil_strip_duration?: number;
  gas_strip_duration?: number;
  blob_path?: string;
  original_filename?: string;
  deck_type?: DeckType;
  anchor_date?: string;
}

/** Full price deck detail returned by GET /price_decks/:id. */
export interface PriceDeckDetail {
  id: number;
  href: string;
  name: string;
  description: string | null;
  methodType: MethodType;
  status: PriceDeckStatus;
  stripDateMode: StripDateMode | null;
  stripPriceDate: string | null;
  config: PriceDeckConfig;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Single price deck result returned by the Price Decks list query. */
export interface PriceDeckResult {
  id: number;
  href: string;
  name: string;
  description: string | null;
  methodType: MethodType;
  status: PriceDeckStatus;
  stripDateMode: StripDateMode | null;
  stripPriceDate: string | null;
  oilPriceDisplay: number | null;
  gasPriceDisplay: number | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}
